import { useEffect, useState } from 'react'
import styled from 'styled-components'

type RaffleCountdownProps = {
  targetDate: string
}

const computeTimeLeft = (targetDate: string) => {
  const diff = Math.max(0, new Date(targetDate).getTime() - Date.now())
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  }
}

const pad = (n: number) => String(n).padStart(2, '0')

export const RaffleCountdown = ({ targetDate }: RaffleCountdownProps) => {
  const [timeLeft, setTimeLeft] = useState(() => computeTimeLeft(targetDate))
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    const interval = setInterval(() => {
      setTimeLeft(computeTimeLeft(targetDate))
    }, 1000)
    return () => clearInterval(interval)
  }, [targetDate])

  const allSegments = [
    { value: timeLeft.days, label: 'D', formatted: String(timeLeft.days) },
    { value: timeLeft.hours, label: 'H', formatted: pad(timeLeft.hours) },
    { value: timeLeft.minutes, label: 'M', formatted: pad(timeLeft.minutes) },
    { value: timeLeft.seconds, label: 'S', formatted: pad(timeLeft.seconds) },
  ]

  // Drop leading segments whose value is 0, but always keep at least M and S.
  const minStartIndex = allSegments.length - 2
  let startIndex = 0
  while (startIndex < minStartIndex && allSegments[startIndex].value === 0) {
    startIndex++
  }
  const segments = allSegments.slice(startIndex)

  if (!mounted) {
    return (
      <Wrapper data-qa="raffle-countdown">
        {segments.map((seg, i) => (
          <span key={seg.label}>
            {i > 0 && ' : '}
            <Digit>--</Digit>
            <Digit>{seg.label}</Digit>
          </span>
        ))}
      </Wrapper>
    )
  }

  return (
    <Wrapper data-qa="raffle-countdown">
      {segments.map((seg, i) => (
        <span key={seg.label}>
          {i > 0 && ' : '}
          <Digit>{seg.formatted}</Digit>
          <Digit>{seg.label}</Digit>
        </span>
      ))}
    </Wrapper>
  )
}

const Wrapper = styled.span`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1;
  color: inherit;
  font-variant-numeric: tabular-nums;
`

const Digit = styled.span`
  font-weight: 500;
`
